import React, { useState, useEffect } from 'react';
import { Navbar } from '../components/Navbar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../utils/api';
import { User, Mail, Phone, MapPin, Globe, Gift, Copy, Users, DollarSign, Clock, Lock, Loader2, ShieldCheck } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useCurrency } from '../hooks/useCurrency';

const Profile = () => {
  const { sym } = useCurrency();
  const [user, setUser] = useState(null);
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [hasPin, setHasPin] = useState(false);
  const [pinForm, setPinForm] = useState({ current_pin: '', new_pin: '', confirm_pin: '' });
  const [pinLoading, setPinLoading] = useState(false);
  const [pinResetStep, setPinResetStep] = useState(null); // null | 'sending' | 'otp' | 'done'
  const [otpCode, setOtpCode] = useState('');
  const [resetNewPin, setResetNewPin] = useState('');
  const [resetConfirmPin, setResetConfirmPin] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await api.get('/auth/me');
        setUser(response.data);
      } catch (error) {
        toast.error('Failed to fetch profile');
      } finally {
        setLoading(false);
      }
    };
    const fetchReferral = async () => {
      try {
        const response = await api.get('/referral');
        setReferral(response.data);
      } catch (error) {
        toast.error('Failed to load referral data');
      }
    };
    const checkPin = async () => {
      try {
        const res = await api.get('/auth/has-pin');
        setHasPin(res.data.has_pin);
      } catch (error) {
        toast.error('Failed to check PIN status');
      }
    };
    fetchUser();
    fetchReferral();
    checkPin();
  }, []);

  const handleSetPin = async (e) => {
    e.preventDefault();
    if (pinForm.new_pin !== pinForm.confirm_pin) {
      toast.error('PINs do not match');
      return;
    }
    if (pinForm.new_pin.length !== 4 || !/^\d{4}$/.test(pinForm.new_pin)) {
      toast.error('PIN must be exactly 4 digits');
      return;
    }
    setPinLoading(true);
    try {
      if (hasPin) {
        await api.post(`/auth/change-pin?current_pin=${pinForm.current_pin}&new_pin=${pinForm.new_pin}`);
        toast.success('Transaction PIN changed!');
      } else {
        await api.post(`/auth/set-pin?pin=${pinForm.new_pin}`);
        toast.success('Transaction PIN set!');
        setHasPin(true);
      }
      setPinForm({ current_pin: '', new_pin: '', confirm_pin: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update PIN');
    }
    setPinLoading(false);
  };

  const handleForgotPin = async () => {
    setPinResetStep('sending');
    try {
      const res = await api.post('/auth/forgot-pin');
      toast.success(res.data.message);
      setPinResetStep('otp');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset code');
      setPinResetStep(null);
    }
  };

  const handleResetPin = async (e) => {
    e.preventDefault();
    if (resetNewPin !== resetConfirmPin) {
      toast.error('PINs do not match');
      return;
    }
    if (resetNewPin.length !== 4 || !/^\d{4}$/.test(resetNewPin)) {
      toast.error('PIN must be exactly 4 digits');
      return;
    }
    if (otpCode.length !== 6) {
      toast.error('Enter the 6-digit code from your email');
      return;
    }
    setResetLoading(true);
    try {
      const res = await api.post(`/auth/reset-pin?otp=${otpCode}&new_pin=${resetNewPin}`);
      toast.success(res.data.message);
      setPinResetStep(null);
      setOtpCode('');
      setResetNewPin('');
      setResetConfirmPin('');
      setHasPin(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    }
    setResetLoading(false);
  };

  const copyLink = () => {
    if (referral?.referral_link) {
      navigator.clipboard.writeText(referral.referral_link);
      toast.success('Referral link copied!');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    setPwLoading(true);
    try {
      const res = await api.post('/auth/change-password', pwForm);
      toast.success(res.data.message);
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(e => e.msg?.replace('Value error, ', '')).join('. '));
      } else {
        toast.error(detail || 'Failed to change password');
      }
    } finally {
      setPwLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="profile-page">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight mb-2" style={{fontFamily: 'DM Sans'}} data-testid="page-heading">Profile</h1>
          <p className="text-muted-foreground" data-testid="page-description">Your account information</p>
        </div>

        <div className="space-y-6">
          <Card className="border shadow-sm" data-testid="profile-card">
            <CardHeader>
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-full bg-primary flex items-center justify-center">
                  <User className="h-8 w-8 text-white" />
                </div>
                <div>
                  <CardTitle className="text-2xl" data-testid="user-name">{user?.first_name} {user?.surname}</CardTitle>
                  <CardDescription data-testid="user-email">{user?.email}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card className="border shadow-sm" data-testid="personal-info-card">
            <CardHeader>
              <CardTitle data-testid="personal-info-title">Personal Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <User className="h-4 w-4" />
                    <span>First Name</span>
                  </div>
                  <p className="font-medium" data-testid="first-name">{user?.first_name}</p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <User className="h-4 w-4" />
                    <span>Surname</span>
                  </div>
                  <p className="font-medium" data-testid="surname">{user?.surname}</p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    <span>Email</span>
                  </div>
                  <p className="font-medium" data-testid="email">{user?.email}</p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="h-4 w-4" />
                    <span>Phone Number</span>
                  </div>
                  <p className="font-medium" data-testid="phone">{user?.phone_number}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border shadow-sm" data-testid="address-card">
            <CardHeader>
              <CardTitle data-testid="address-title">Address Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  <span>Home Address</span>
                </div>
                <p className="font-medium" data-testid="address">{user?.address}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">City</p>
                  <p className="font-medium" data-testid="city">{user?.city}</p>
                </div>

                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">State</p>
                  <p className="font-medium" data-testid="state">{user?.state}</p>
                </div>

                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Zip Code</p>
                  <p className="font-medium" data-testid="zip-code">{user?.zip_code}</p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Globe className="h-4 w-4" />
                  <span>Country</span>
                </div>
                <p className="font-medium" data-testid="country">{user?.country}</p>
              </div>
            </CardContent>
          </Card>

          {referral && (
            <Card className="border shadow-sm border-primary/20 bg-primary/5" data-testid="referral-card">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Gift className="h-5 w-5 text-primary" />
                  <CardTitle data-testid="referral-title">Invite Friends & Earn</CardTitle>
                </div>
                <CardDescription>Share your link — you both get <strong>{sym}2.00</strong> when they make their first deposit</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-background border rounded-lg px-4 py-2.5 font-mono text-sm truncate" data-testid="referral-link">
                    {referral.referral_link}
                  </div>
                  <Button variant="outline" size="sm" className="rounded-full shrink-0" onClick={copyLink} data-testid="copy-link-button">
                    <Copy className="h-4 w-4 mr-2" /> Copy
                  </Button>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-background rounded-lg border" data-testid="total-referrals-stat">
                    <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                      <Users className="h-4 w-4" />
                    </div>
                    <p className="text-2xl font-bold">{referral.completed_referrals}</p>
                    <p className="text-xs text-muted-foreground">Referrals</p>
                  </div>
                  <div className="text-center p-3 bg-background rounded-lg border" data-testid="pending-referrals-stat">
                    <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                      <Clock className="h-4 w-4" />
                    </div>
                    <p className="text-2xl font-bold">{referral.pending_signups}</p>
                    <p className="text-xs text-muted-foreground">Pending</p>
                  </div>
                  <div className="text-center p-3 bg-background rounded-lg border" data-testid="total-earned-stat">
                    <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
                      <DollarSign className="h-4 w-4" />
                    </div>
                    <p className="text-2xl font-bold text-primary">{sym}{referral.total_earned.toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground">Earned</p>
                  </div>
                </div>

                {referral.referrals.length > 0 && (
                  <div>
                    <p className="text-sm font-medium mb-2">Recent Referrals</p>
                    <div className="space-y-2">
                      {referral.referrals.map((ref, i) => (
                        <div key={ref.referred_email || i} className="flex items-center justify-between p-2 bg-background rounded-lg border text-sm" data-testid={`referral-item-${i}`}>
                          <span className="text-muted-foreground">{ref.referred_email}</span>
                          <span className="text-primary font-medium">+{sym}{ref.bonus_amount.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card className="border shadow-sm" data-testid="transaction-pin-card">
            <CardHeader>
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-primary" />
                <CardTitle data-testid="pin-title">{hasPin ? 'Change Transaction PIN' : 'Set Transaction PIN'}</CardTitle>
              </div>
              <CardDescription>
                {hasPin
                  ? 'Update your 4-digit PIN used to authorize transactions'
                  : 'Set a 4-digit PIN to secure your send money and withdrawal transactions'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSetPin} className="space-y-4">
                {hasPin && (
                  <div className="space-y-2">
                    <Label htmlFor="current_pin">Current PIN</Label>
                    <Input
                      id="current_pin"
                      type="password"
                      inputMode="numeric"
                      maxLength={4}
                      placeholder="****"
                      value={pinForm.current_pin}
                      onChange={(e) => setPinForm({ ...pinForm, current_pin: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                      required
                      data-testid="current-pin-input"
                    />
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="new_pin">{hasPin ? 'New PIN' : 'PIN'}</Label>
                    <Input
                      id="new_pin"
                      type="password"
                      inputMode="numeric"
                      maxLength={4}
                      placeholder="4 digits"
                      value={pinForm.new_pin}
                      onChange={(e) => setPinForm({ ...pinForm, new_pin: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                      required
                      data-testid="new-pin-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm_pin">Confirm PIN</Label>
                    <Input
                      id="confirm_pin"
                      type="password"
                      inputMode="numeric"
                      maxLength={4}
                      placeholder="4 digits"
                      value={pinForm.confirm_pin}
                      onChange={(e) => setPinForm({ ...pinForm, confirm_pin: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                      required
                      data-testid="confirm-pin-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={pinLoading}
                  className="rounded-full bg-primary hover:bg-primary/90"
                  data-testid="save-pin-button"
                >
                  {pinLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</> : (hasPin ? 'Change PIN' : 'Set PIN')}
                </Button>
                {hasPin && !pinResetStep && (
                  <button
                    type="button"
                    onClick={handleForgotPin}
                    className="text-xs text-primary hover:underline mt-1"
                    data-testid="forgot-pin-link"
                  >
                    Forgot your PIN?
                  </button>
                )}
              </form>

              {pinResetStep === 'sending' && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-4">
                  <Loader2 className="h-4 w-4 animate-spin" /> Sending verification code to your email...
                </div>
              )}

              {pinResetStep === 'otp' && (
                <form onSubmit={handleResetPin} className="mt-4 pt-4 border-t space-y-4" data-testid="pin-reset-otp-form">
                  <p className="text-sm text-muted-foreground">Enter the 6-digit code sent to your email and choose a new PIN.</p>
                  <div className="space-y-2">
                    <Label htmlFor="otp_code">Verification Code</Label>
                    <Input
                      id="otp_code"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="123456"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      required
                      data-testid="otp-code-input"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="reset_new_pin">New PIN</Label>
                      <Input
                        id="reset_new_pin"
                        type="password"
                        inputMode="numeric"
                        maxLength={4}
                        placeholder="4 digits"
                        value={resetNewPin}
                        onChange={(e) => setResetNewPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        required
                        data-testid="reset-new-pin-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="reset_confirm_pin">Confirm New PIN</Label>
                      <Input
                        id="reset_confirm_pin"
                        type="password"
                        inputMode="numeric"
                        maxLength={4}
                        placeholder="4 digits"
                        value={resetConfirmPin}
                        onChange={(e) => setResetConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        required
                        data-testid="reset-confirm-pin-input"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setPinResetStep(null); setOtpCode(''); setResetNewPin(''); setResetConfirmPin(''); }}
                      data-testid="cancel-reset-btn"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={resetLoading}
                      className="rounded-full bg-primary hover:bg-primary/90"
                      data-testid="confirm-reset-pin-btn"
                    >
                      {resetLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Resetting...</> : 'Reset PIN'}
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>

          <Card className="border shadow-sm" data-testid="change-password-card">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-primary" />
                <CardTitle data-testid="change-password-title">Change Password</CardTitle>
              </div>
              <CardDescription>Update your account password</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="current_password">Current Password</Label>
                  <Input
                    id="current_password"
                    type="password"
                    value={pwForm.current_password}
                    onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                    required
                    data-testid="current-password-input"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="new_password">New Password</Label>
                    <Input
                      id="new_password"
                      type="password"
                      placeholder="Min. 8 characters"
                      value={pwForm.new_password}
                      onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                      required
                      minLength={8}
                      data-testid="new-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm_password">Confirm New Password</Label>
                    <Input
                      id="confirm_password"
                      type="password"
                      value={pwForm.confirm_password}
                      onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })}
                      required
                      minLength={8}
                      data-testid="confirm-new-password-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={pwLoading}
                  className="rounded-full bg-primary hover:bg-primary/90"
                  data-testid="change-password-button"
                >
                  {pwLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Changing...</> : 'Change Password'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Profile;